/**
 * @module utils/runtime-tools
 * @description 런타임 전역 도구 모듈 — window 객체에 앱 전반에서 사용하는
 * 토스트 알림, 에러 로그, 일반 로그 유틸리티를 설치합니다.
 * console.log 대신 window.__APP_LOG__()를 사용하고,
 * 알림은 window.appAlert() 또는 window.__APP_NOTIFY__()를 통해 발행합니다.
 */
const TOAST_EVENT_NAME = 'app:toast'
const MAX_ERROR_LOGS = 200
const TOAST_TYPES = ['success', 'error', 'warning', 'info']

/**
 * 임의의 값을 문자열로 변환합니다.
 * Error 인스턴스는 스택 트레이스를, 객체는 JSON 직렬화 결과를 반환합니다.
 *
 * @param {*} value - 문자열로 변환할 값
 * @returns {string} 변환된 문자열
 */
const toStringValue = (value) => {
  if (value instanceof Error) {
    return value.stack || value.message || String(value)
  }
  if (typeof value === 'object' && value !== null) {
    try {
      return JSON.stringify(value)
    } catch (_error) {
      return '[unserializable-object]'
    }
  }
  return String(value)
}

// Active toast deduplication — tracks displayed toasts by "type::message" key
const _activeToasts = new Map()

/**
 * 토스트 알림 이벤트를 발행합니다.
 * 동일한 type+message 조합의 토스트가 표시 중이면 중복 발행을 방지합니다.
 *
 * @param {object} [options={}] - 알림 옵션
 * @param {string} [options.message=''] - 알림 메시지
 * @param {string} [options.type='info'] - 알림 타입 ('success' | 'error' | 'warning' | 'info')
 * @param {number} [options.duration=2500] - 알림 표시 시간(ms)
 */
export const notify = ({ message = '', type = 'info', duration = 2500 } = {}) => {
  if (typeof window === 'undefined') {
    return
  }

  const normalizedType = typeof type === 'string' ? type.trim().toLowerCase() : ''
  const safeType = TOAST_TYPES.includes(normalizedType) ? normalizedType : 'info'

  const dedupeKey = `${safeType}::${message}`
  if (_activeToasts.has(dedupeKey)) {
    return
  }

  const safeDuration = Number.isFinite(duration) ? duration : 2500
  const timeoutId = setTimeout(() => {
    _activeToasts.delete(dedupeKey)
  }, Math.max(safeDuration, 800))
  _activeToasts.set(dedupeKey, timeoutId)

  window.dispatchEvent(new CustomEvent(TOAST_EVENT_NAME, {
    detail: {
      message: String(message),
      type: safeType,
      duration
    }
  }))
}

// Exported for testing — clears all active toast deduplication state
export const clearActiveToasts = () => {
  _activeToasts.forEach((timeoutId) => clearTimeout(timeoutId))
  _activeToasts.clear()
}

// Exported for testing — returns current active toasts count
export const getActiveToastsSize = () => _activeToasts.size

/**
 * 메시지 내용을 분석하여 적절한 토스트 타입을 추론합니다.
 * 오류/실패 키워드 → 'error', 경고/입력 필요 키워드 → 'warning',
 * 성공/완료 키워드 → 'success', 그 외 → 'info'를 반환합니다.
 *
 * @param {string} message - 분석할 메시지 문자열
 * @returns {'success'|'error'|'warning'|'info'} 추론된 알림 타입
 */
const inferAlertType = (message) => {
  const text = String(message || '').trim()
  if (!text) {
    return 'info'
  }

  const lower = text.toLowerCase()

  // 1) Error/failure first
  if (
    /(error|fail|failed|invalid|forbidden|denied|expired|timeout|exception|not\s+found|실패|오류|만료|불가|유효하지)/i.test(text)
  ) {
    return 'error'
  }

  // 2) Warnings/user-action-needed
  if (
    /(warning|already|retry|required|input|select|agree|already|이미|입력|선택|동의|다시\s*요청|요청해|필수)/i.test(text)
  ) {
    return 'warning'
  }

  // 3) Success state
  if (
    /(success|saved|completed|done|registered|updated|sent|verified|assigned|confirmed|저장|완료|등록|변경|발송|전송|확정|배정|성공|요청되었습니다|처리되었습니다)/i.test(lower)
  ) {
    return 'success'
  }

  return 'info'
}

/**
 * 에러 메시지를 window.__APP_ERROR_LOGS__ 배열에 기록합니다.
 * 최대 200개까지 유지하며 초과 시 오래된 항목을 제거합니다.
 *
 * @param {...*} args - 기록할 에러 메시지 또는 값 (여러 개 전달 가능)
 */
export const reportError = (...args) => {
  if (typeof window === 'undefined') {
    return
  }

  const entry = {
    message: args.map(toStringValue).join(' '),
    at: new Date().toISOString()
  }

  const logs = Array.isArray(window.__APP_ERROR_LOGS__) ? window.__APP_ERROR_LOGS__ : []
  logs.push(entry)
  if (logs.length > MAX_ERROR_LOGS) {
    logs.splice(0, logs.length - MAX_ERROR_LOGS)
  }
  window.__APP_ERROR_LOGS__ = logs
}

/**
 * 일반 로그 메시지를 window.__APP_LOGS__ 배열에 기록합니다.
 * 최대 200개까지 유지하며 초과 시 오래된 항목을 제거합니다.
 *
 * @param {...*} args - 기록할 메시지 또는 값 (여러 개 전달 가능)
 */
export const reportLog = (...args) => {
  if (typeof window === 'undefined') {
    return
  }

  const entry = {
    message: args.map(toStringValue).join(' '),
    at: new Date().toISOString()
  }

  const logs = Array.isArray(window.__APP_LOGS__) ? window.__APP_LOGS__ : []
  logs.push(entry)
  if (logs.length > MAX_ERROR_LOGS) {
    logs.splice(0, logs.length - MAX_ERROR_LOGS)
  }
  window.__APP_LOGS__ = logs
}

/**
 * window 전역 객체에 앱 런타임 도구를 설치합니다.
 * 앱 초기화 시 한 번만 호출해야 합니다.
 *
 * 설치되는 전역 함수:
 * - window.__APP_NOTIFY__(payload) — 토스트 알림 발행
 * - window.__APP_REPORT_ERROR__(...args) — 에러 로그 기록
 * - window.__APP_LOG__(...args) — 일반 로그 기록
 * - window.appAlert(message, type) — 알림 표시 (타입 자동 추론 지원)
 * - window.appConfirm(message) — 브라우저 confirm 다이얼로그
 */
export const installRuntimeTools = () => {
  if (typeof window === 'undefined') {
    return
  }

  window.__APP_NOTIFY__ = (payload) => {
    if (typeof payload === 'string') {
      notify({ message: payload })
      return
    }
    notify(payload || {})
  }

  window.__APP_REPORT_ERROR__ = (...args) => reportError(...args)
  window.__APP_LOG__ = (...args) => reportLog(...args)
  window.appAlert = (message, type) => {
    const normalizedType = typeof type === 'string' ? type.trim().toLowerCase() : ''
    const safeType = TOAST_TYPES.includes(normalizedType) ? normalizedType : inferAlertType(message)
    notify({ message, type: safeType })
  }
  // eslint-disable-next-line no-alert
  window.appConfirm = (message) => window.confirm(message)
}
